import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Button, Badge, Alert } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config';
import { WorkflowCustomField, TicketCustomFieldValue, PurchaseCustomFieldValue, OpportunityModuleCustomFieldValue, User, Group, DocumentInstance, PurchaseAttachment } from '../types';
import { useModal } from '../context/ModalContext';
import { downloadFile, normalizeStoragePathForDownload, openFileInNewTab } from '../utils/fileDownload';
import { getSingleCustomFieldFileDisplayName, parseCustomFieldFilePathsJson, ParsedCustomFieldFileEntry } from '../utils/customFieldFileDisplay';
import CustomTableField from './CustomTableField';
import { createDefaultTableValue, parseTableValue, serializeTableValue, TableCustomFieldValue } from '../utils/tableCustomField';
import {
  ATTACHMENT_ACCEPT,
  validateAttachmentFile,
  validateAttachmentFiles,
} from '../utils/uploadValidation';
import SupplierFieldControl from './SupplierFieldControl';
import { isSupplierField as isSupplierCustomField, validateSupplierSelectionValue } from '../utils/supplierSelection';
import { shouldShowConditionalField } from '../utils/conditionalField';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../utils/currencyInput';
import { getAuthToken } from '../utils/authToken';
import {
  hasCheckFieldPersistedValue,
  hydrateCustomFieldTextValue,
  serializeCheckFieldValue,
} from '../utils/purchaseCustomFieldValues';
import {
  upsertCustomFieldValue,
  type CustomFieldUpsertArea,
} from '../utils/purchaseCustomFieldUpsert';
import { orderFieldsForSave } from '../utils/customFieldSaveOrder';
import { serverValueSignature, shouldAdoptServerFieldValue } from '../utils/customFieldSync';

type CustomFieldValue = TicketCustomFieldValue | PurchaseCustomFieldValue | OpportunityModuleCustomFieldValue;
type TechnicalProposalDecision = 'approved' | 'clarifications_for_approval' | 'rejected';
type TechnicalProposalSource = 'upload' | 'existing_attachment';

interface TechnicalProposalReviewValue {
  decision: TechnicalProposalDecision;
  opinion: string;
  proposal_source: TechnicalProposalSource;
  purchase_attachment_id?: number;
}

interface CustomFieldsEditorProps {
  ticketId: number;
  workflowId: number;
  workflowName?: string;
  currentStep?: string;
  customFields: WorkflowCustomField[];
  allCustomFields?: WorkflowCustomField[];
  customFieldValues: CustomFieldValue[];
  onSave: () => void;
  canEdit: boolean;
  assignedGroupId?: number;  // ID do grupo atribuído ao ticket (para buscar analistas)
  areaType?: 'tickets' | 'purchases' | 'opportunities-module';
  purchaseAttachments?: PurchaseAttachment[];
}

const normalizeFieldToken = (value?: string | null): string => {
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const normalizeOptionToken = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const CHECKLIST_FIELD_MARKERS = ['checklist'];
const CHECKLIST_ATTACHMENT_PREFIX = 'anexo_q_checklist_auto_';
const MEMORIAL_DOCUMENT_MARKERS = ['memorial descritivo', 'memorial'];
const STRATEGIC_PLAN_DOCUMENT_MARKERS = ['strategic planning', 'strategic plan', 'strategic'];

const isChecklistRelatedField = (field: WorkflowCustomField): boolean => {
  const tokens = [
    normalizeFieldToken(field.name),
    normalizeFieldToken(field.label),
    normalizeFieldToken(field.group_name),
    normalizeFieldToken(field.description),
  ];
  return tokens.some((token) =>
    CHECKLIST_FIELD_MARKERS.some((marker) => token.includes(marker))
  );
};

const isChecklistAttachmentField = (field: WorkflowCustomField): boolean => {
  const normalizedName = normalizeFieldToken(field.name);
  return (
    (field.field_type === 'file' || field.field_type === 'files') &&
    normalizedName.startsWith(CHECKLIST_ATTACHMENT_PREFIX)
  );
};

const inferPlatformDocumentContext = (field: WorkflowCustomField): 'memorial' | 'strategic' | null => {
  const tokens = [
    normalizeFieldToken(field.name),
    normalizeFieldToken(field.label),
    normalizeFieldToken(field.group_name),
    normalizeFieldToken(field.description),
  ];

  const hasMarker = (markers: string[]) =>
    tokens.some((token) => markers.some((marker) => token.includes(normalizeFieldToken(marker))));

  if (hasMarker(MEMORIAL_DOCUMENT_MARKERS)) return 'memorial';
  if (hasMarker(STRATEGIC_PLAN_DOCUMENT_MARKERS)) return 'strategic';
  return null;
};

const filterPlatformDocumentsForField = (
  field: WorkflowCustomField,
  documentInstances: DocumentInstance[]
): DocumentInstance[] => {
  const finalizedDocs = documentInstances.filter((doc) => doc.status === 'finalized');

  // Regra principal: se o workflow configurou tipo de documento específico, respeitar estritamente.
  if (field.document_type_id) {
    return finalizedDocs.filter((doc) => doc.document_type_id === field.document_type_id);
  }

  // Fallback para campos legados sem document_type_id: inferir contexto pelo nome/rótulo do campo.
  const context = inferPlatformDocumentContext(field);
  if (!context) return finalizedDocs;

  const markers =
    context === 'memorial' ? MEMORIAL_DOCUMENT_MARKERS : STRATEGIC_PLAN_DOCUMENT_MARKERS;

  const contextDocs = finalizedDocs.filter((doc) => {
    const normalizedTypeName = normalizeFieldToken(doc.document_type?.name || '');
    const normalizedTitle = normalizeFieldToken(doc.title || '');
    return markers.some(
      (marker) =>
        normalizedTypeName.includes(normalizeFieldToken(marker)) ||
        normalizedTitle.includes(normalizeFieldToken(marker))
    );
  });

  // Evita quebrar campos antigos caso não exista metadado suficiente para inferência.
  return contextDocs.length > 0 ? contextDocs : finalizedDocs;
};

const formatPlatformDocumentLabel = (doc: DocumentInstance): string => {
  const typeName = doc.document_type?.name || 'Documento';
  const title = doc.title ? ` - ${doc.title}` : '';
  const createdAt = doc.created_at ? ` (${new Date(doc.created_at).toLocaleDateString('pt-BR')})` : '';
  return `${typeName}${title}${createdAt}`;
};

const normalizeSingleChoiceValue = (raw: unknown): string => {
  if (raw === null || raw === undefined) return '';
  if (Array.isArray(raw)) {
    const first = raw.find((item) => String(item ?? '').trim() !== '');
    return first === undefined ? '' : String(first).trim();
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const first = parsed.find((item) => String(item ?? '').trim() !== '');
        return first === undefined ? '' : String(first).trim();
      }
      if (parsed === null || parsed === undefined) return '';
      if (typeof parsed === 'boolean') return parsed ? 'true' : 'false';
      return String(parsed).trim();
    } catch {
      return trimmed;
    }
  }
  if (typeof raw === 'boolean') return raw ? 'true' : 'false';
  return String(raw).trim();
};

const resolveSingleChoiceOptionValue = (options: string[], raw: unknown): string => {
  const candidate = normalizeSingleChoiceValue(raw);
  if (!candidate) return '';
  const exactMatch = options.find((option) => option === candidate);
  if (exactMatch) return exactMatch;
  const candidateToken = normalizeOptionToken(candidate);
  const normalizedMatch = options.find(
    (option) => normalizeOptionToken(option) === candidateToken
  );
  return normalizedMatch || candidate;
};

const CustomFieldsEditor: React.FC<CustomFieldsEditorProps> = ({
  ticketId,
  workflowId,
  workflowName,
  currentStep,
  customFields,
  allCustomFields,
  customFieldValues,
  onSave,
  canEdit,
  assignedGroupId,
  areaType = 'tickets',
  purchaseAttachments = [],
}) => {
  const isOpportunitiesModule = areaType === 'opportunities-module';
  const upsertArea: CustomFieldUpsertArea =
    areaType === 'opportunities-module'
      ? 'opportunities-module'
      : areaType === 'tickets'
        ? 'tickets'
        : 'purchases';
  const [values, setValues] = useState<{ [fieldId: number]: any }>({});
  const [files, setFiles] = useState<{ [fieldId: number]: File | File[] | null }>({});
  const [loading, setLoading] = useState(false);
  const { showAlert } = useModal();
  const initializedRef = useRef<Set<number>>(new Set());
  const prevFieldsKeyRef = useRef<string>('');
  /** Última assinatura do valor remoto por campo, para detectar mudanças vindas do servidor. */
  const lastSyncedServerValueRef = useRef<{ [fieldId: number]: string }>({});
  const [users, setUsers] = useState<User[]>([]);
  const [approverUsers, setApproverUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [analystUsers, setAnalystUsers] = useState<User[]>([]);
  const [searchUsers, setSearchUsers] = useState<{ [fieldId: number]: string }>({});
  const [searchGroups, setSearchGroups] = useState<{ [fieldId: number]: string }>({});
  const [filteredUsers, setFilteredUsers] = useState<{ [fieldId: number]: User[] }>({});
  const [filteredGroups, setFilteredGroups] = useState<{ [fieldId: number]: Group[] }>({});
  const [documentInstances, setDocumentInstances] = useState<DocumentInstance[]>([]);
  const [platformDocumentMode, setPlatformDocumentMode] = useState<{ [fieldId: number]: 'upload' | 'platform' }>({});
  const [platformDocumentSelection, setPlatformDocumentSelection] = useState<{ [fieldId: number]: number | null }>({});
  const [savingPlatformDocument, setSavingPlatformDocument] = useState<{ [fieldId: number]: boolean }>({});

  const parseTechnicalProposalValue = (rawValue?: string): TechnicalProposalReviewValue | null => {
    if (!rawValue || !rawValue.trim()) return null;
    try {
      const parsed = JSON.parse(rawValue);
      if (!parsed || typeof parsed !== 'object') return null;
      const decision = String(parsed.decision || '').trim();
      const opinion = String(parsed.opinion || '').trim();
      const proposalSource = String(parsed.proposal_source || '').trim();
      if (!decision || !proposalSource) return null;
      const normalized: TechnicalProposalReviewValue = {
        decision: decision as TechnicalProposalDecision,
        opinion,
        proposal_source: proposalSource as TechnicalProposalSource,
      };
      if (parsed.purchase_attachment_id !== undefined && parsed.purchase_attachment_id !== null) {
        const attachmentId = Number(parsed.purchase_attachment_id);
        if (!Number.isNaN(attachmentId) && attachmentId > 0) {
          normalized.purchase_attachment_id = attachmentId;
        }
      }
      return normalized;
    } catch {
      return null;
    }
  };

  // Criar uma chave única baseada nos IDs dos campos para detectar mudanças reais
  const fieldsKey = customFields.map(f => f.id).sort().join(',');
  const conditionalContextFields =
    allCustomFields && allCustomFields.length > 0 ? allCustomFields : customFields;

  const hydrateStoredFieldValue = (
    field: WorkflowCustomField,
    existingValue?: CustomFieldValue
  ): unknown => {
    const dbValue = existingValue?.text_value ?? '';
    if (!existingValue) {
      return undefined;
    }
    if ((field.field_type === 'list' || field.field_type === 'combo') && !field.allow_multiple) {
      return normalizeSingleChoiceValue(dbValue);
    }
    if ((field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) && field.allow_multiple) {
      try {
        return dbValue ? JSON.parse(String(dbValue)) : [];
      } catch {
        return dbValue ? [dbValue] : [];
      }
    }
    if (field.field_type === 'table' && dbValue) {
      return parseTableValue(String(dbValue));
    }
    if (field.field_type === 'check' && !field.allow_multiple && dbValue !== '') {
      const hydratedCheckValue = hydrateCustomFieldTextValue(String(dbValue));
      return hydratedCheckValue === null || hydratedCheckValue === undefined
        ? ''
        : String(hydratedCheckValue);
    }
    if (field.field_type === 'technical_proposal_review') {
      return parseTechnicalProposalValue(String(dbValue)) || {
        decision: 'approved',
        opinion: '',
        proposal_source: existingValue?.file_path ? 'upload' : 'existing_attachment',
      };
    }
    return dbValue;
  };

  const conditionalValues = useMemo(() => {
    const mergedValues: Record<number, unknown> = { ...values };
    conditionalContextFields.forEach((field) => {
      // Só recorre ao banco para campos que o editor ainda não hidratou (ex.: campos de
      // outra aba). Um campo limpo pelo usuário precisa contar como vazio nas condições.
      if (mergedValues[field.id] !== undefined) {
        return;
      }
      const existing = customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id);
      const hydrated = hydrateStoredFieldValue(field, existing);
      if (hydrated !== undefined) {
        mergedValues[field.id] = hydrated;
      }
    });
    return mergedValues;
  }, [values, conditionalContextFields, customFieldValues]);

  // Buscar usuários e grupos quando necessário
  useEffect(() => {
    const hasUserField = customFields.some(f => f.field_type === 'user');
    const hasApproverField = customFields.some(f => f.field_type === 'approver' || f.field_type === 'contract_manager_approver');
    const hasGroupField = customFields.some(f => f.field_type === 'group');
    const hasAnalystField = customFields.some(f => f.field_type === 'analyst');
    const hasPlatformDocumentField = customFields.some(f => f.field_type === 'platform_document');

    const fetchData = async () => {
      if (hasUserField) {
        try {
          const response = await axios.get(`${API_URL}/api/users`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          });
          setUsers(response.data.filter((u: User) => u.is_active));
        } catch (error) {
          console.error('Erro ao buscar usuários:', error);
        }
      }

      if (hasApproverField) {
        try {
          const response = await axios.get(`${API_URL}/api/users/approvers`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          });
          setApproverUsers(response.data.filter((u: User) => u.is_active));
        } catch (error) {
          console.error('Erro ao buscar aprovadores:', error);
        }
      }

      if (hasGroupField) {
        try {
          const response = await axios.get(`${API_URL}/api/groups`, {
            params: { limit: 1000 },
          });
          setGroups(response.data.filter((g: Group) => g.is_active));
        } catch (error) {
          console.error('Erro ao buscar grupos:', error);
        }
      }

      if (hasAnalystField && assignedGroupId) {
        try {
          const response = await axios.get(`${API_URL}/api/groups/${assignedGroupId}`);
          const groupMembers = response.data.members || [];
          setAnalystUsers(groupMembers.map((m: any) => ({
            id: m.user_id,
            name: m.user_name,
            email: m.user_email,
            is_active: true
          })));
        } catch (error) {
          console.error('Erro ao buscar analistas do grupo:', error);
        }
      }

      if (hasPlatformDocumentField && areaType === 'purchases') {
        try {
          const response = await axios.get(`${API_URL}/api/documents/instances/available`, {
            params: { status: 'finalized' },
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          });
          setDocumentInstances(response.data);
        } catch (error) {
          console.error('Erro ao buscar documentos:', error);
        }
      }
    };

    if (customFields.length > 0) {
      fetchData();
    }
  }, [customFields, assignedGroupId, areaType]);

  useEffect(() => {
    const nextSelection: { [fieldId: number]: number | null } = {};
    customFields.forEach((field) => {
      if (field.field_type !== 'platform_document') return;
      const existingValue = customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id);
      const purchaseValue =
        existingValue && 'document_instance_id' in existingValue
          ? (existingValue as PurchaseCustomFieldValue)
          : undefined;
      if (purchaseValue?.document_instance_id) {
        nextSelection[field.id] = purchaseValue.document_instance_id;
      }
    });
    setPlatformDocumentSelection((prev) => ({ ...prev, ...nextSelection }));
  }, [customFieldValues, customFields]);

  useEffect(() => {
    // Só inicializar campos novos, não resetar campos existentes
    const fieldsChanged = prevFieldsKeyRef.current !== fieldsKey;
    
    if (fieldsChanged) {
      setValues(prevValues => {
        const newValues = { ...prevValues };
        
        customFields.forEach((field) => {
          // Só inicializar se o campo ainda não foi inicializado
          if (!initializedRef.current.has(field.id)) {
            const existingValue = customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id);
            
            if (field.field_type === 'file' || field.field_type === 'files') {
              // Arquivos são tratados separadamente no setFiles
            } else if (existingValue) {
              // Para checkboxes com múltiplas opções, fazer parse do JSON
              if (field.field_type === 'check' && field.allow_multiple && existingValue.text_value) {
                try {
                  newValues[field.id] = JSON.parse(existingValue.text_value);
                } catch {
                  newValues[field.id] = [];
                }
              } else if (field.field_type === 'table' && existingValue.text_value) {
                newValues[field.id] = parseTableValue(existingValue.text_value);
              } else if (field.field_type === 'technical_proposal_review') {
                newValues[field.id] = parseTechnicalProposalValue(existingValue.text_value) || {
                  decision: 'approved',
                  opinion: '',
                  proposal_source: existingValue.file_path ? 'upload' : 'existing_attachment',
                };
              } else if (field.field_type === 'check' && existingValue.text_value !== null && existingValue.text_value !== undefined) {
                newValues[field.id] = hydrateCustomFieldTextValue(String(existingValue.text_value));
              } else {
                newValues[field.id] = existingValue.text_value ?? '';
              }
            } else {
              if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
                newValues[field.id] = field.allow_multiple ? [] : '';
              } else if (field.field_type === 'table') {
                newValues[field.id] = createDefaultTableValue();
              } else if (field.field_type === 'technical_proposal_review') {
                newValues[field.id] = {
                  decision: 'approved',
                  opinion: '',
                  proposal_source: 'upload',
                };
              } else {
                newValues[field.id] = '';
              }
            }
            
            initializedRef.current.add(field.id);
          }
        });
        
        return newValues;
      });
      
      setFiles(prevFiles => {
        const newFiles = { ...prevFiles };
        
        customFields.forEach((field) => {
          if (field.field_type === 'file' || field.field_type === 'files' || field.field_type === 'technical_proposal_review') {
            if (!initializedRef.current.has(field.id)) {
              newFiles[field.id] = null;
              initializedRef.current.add(field.id);
            }
          }
        });
        
        return newFiles;
      });
      
      prevFieldsKeyRef.current = fieldsKey;
    }
  }, [fieldsKey, customFields, customFieldValues]);

  // Atualizar valores quando o servidor mudar (carga inicial, gravação, alteração de outra
  // pessoa). Comparar com o valor local não serve como critério: enquanto o usuário edita,
  // o valor local diverge do banco de propósito.
  const valuesKey = useMemo(() => 
    customFieldValues.map(v => `${v.custom_field_id}:${v.text_value ?? ''}`).join(','),
    [customFieldValues]
  );

  const customFieldIdsKey = useMemo(
    () => customFields.map((f) => f.id).join(','),
    [customFields]
  );

  useEffect(() => {
    // A assinatura remota é lida e registrada fora do updater: sob StrictMode o updater pode
    // ser executado duas vezes, e a segunda passada já veria a assinatura como sincronizada.
    const previousSignatures: { [fieldId: number]: string | undefined } = {};
    customFields.forEach((field) => {
      if (field.field_type === 'file' || field.field_type === 'files') return;
      const existingValue = customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id);
      previousSignatures[field.id] = lastSyncedServerValueRef.current[field.id];
      lastSyncedServerValueRef.current[field.id] = serverValueSignature(existingValue);
    });

    setValues(prevValues => {
      const newValues = { ...prevValues };
      let hasChanges = false;

      customFields.forEach((field) => {
        if (field.field_type !== 'file' && field.field_type !== 'files') {
          const existingValue = customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id);
          const dbValue = existingValue?.text_value ?? '';
          const currentValue = prevValues[field.id];

          const adoptServerValue = shouldAdoptServerFieldValue({
            currentValue,
            serverSignature: serverValueSignature(existingValue),
            lastSyncedSignature: previousSignatures[field.id],
          });

          if (existingValue) {
            let parsedDbValue: string | string[] | TableCustomFieldValue | TechnicalProposalReviewValue = dbValue;
            // Para campos de lista/combo/check com múltiplos valores, fazer parse
            if ((field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) && field.allow_multiple && dbValue) {
              try {
                parsedDbValue = JSON.parse(dbValue);
              } catch {
                parsedDbValue = dbValue ? [dbValue] : [];
              }
            } else if ((field.field_type === 'list' || field.field_type === 'combo') && !field.allow_multiple) {
              parsedDbValue = normalizeSingleChoiceValue(dbValue);
            } else if (field.field_type === 'table' && dbValue) {
              parsedDbValue = parseTableValue(dbValue);
            } else if (field.field_type === 'check' && !field.allow_multiple && dbValue !== '') {
              const hydratedCheckValue = hydrateCustomFieldTextValue(dbValue);
              parsedDbValue =
                hydratedCheckValue === null || hydratedCheckValue === undefined
                  ? ''
                  : String(hydratedCheckValue);
            } else if (field.field_type === 'technical_proposal_review') {
              parsedDbValue = parseTechnicalProposalValue(dbValue) || {
                decision: 'approved',
                opinion: '',
                proposal_source: existingValue?.file_path ? 'upload' : 'existing_attachment',
              };
            }
            
            // Só sobrescreve o estado local quando o próprio servidor mudou; caso contrário
            // a edição em andamento seria descartada a cada re-render.
            if (adoptServerValue && JSON.stringify(currentValue) !== JSON.stringify(parsedDbValue)) {
              newValues[field.id] = parsedDbValue;
              hasChanges = true;
            }
          } else if (currentValue === undefined) {
            // Inicializar campo vazio se não existe valor no banco e ainda não foi inicializado
            if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
              newValues[field.id] = field.allow_multiple ? [] : '';
            } else if (field.field_type === 'table') {
              newValues[field.id] = createDefaultTableValue();
            } else if (field.field_type === 'technical_proposal_review') {
              newValues[field.id] = {
                decision: 'approved',
                opinion: '',
                proposal_source: 'upload',
              };
            } else {
              newValues[field.id] = '';
            }
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newValues : prevValues;
    });
  }, [valuesKey, customFieldIdsKey, customFields, customFieldValues]);


  const handleTextChange = (fieldId: number, value: string) => {
    setValues(prevValues => ({ ...prevValues, [fieldId]: value }));
  };

  const handleSelectChange = (fieldId: number, value: string | string[]) => {
    setValues(prevValues => ({ ...prevValues, [fieldId]: value }));
  };

  const handleFileChange = (fieldId: number, file: File | FileList | null) => {
    if (!file) {
      setFiles({ ...files, [fieldId]: null });
      return;
    }

    const field = customFields.find((f) => f.id === fieldId);
    if (!field) return;

    if (field.field_type === 'files') {
      // Múltiplos arquivos
      const fileArray = file instanceof FileList ? Array.from(file) : [file];
      const filesError = validateAttachmentFiles(fileArray);
      if (filesError) {
        showAlert({
          title: 'Arquivo inválido',
          message: filesError,
          variant: 'warning',
        });
        setFiles({ ...files, [fieldId]: null });
        return;
      }
      setFiles({ ...files, [fieldId]: fileArray });
    } else {
      // Arquivo único
      const singleFile = file instanceof FileList ? file[0] : file;
      const fileError = validateAttachmentFile(singleFile);
      if (fileError) {
        showAlert({
          title: 'Arquivo inválido',
          message: fileError,
          variant: 'warning',
        });
        setFiles({ ...files, [fieldId]: null });
        return;
      }
      setFiles({ ...files, [fieldId]: singleFile });
    }
  };

  const clearStoredFileValue = async (field: WorkflowCustomField) => {
    if (!canEdit) return;

    const putUrl = isOpportunitiesModule
      ? `${API_URL}/api/purchases/opportunities/module/custom-fields/${field.id}`
      : `${API_URL}/api/${areaType === 'purchases' ? 'purchases' : 'tickets'}/${ticketId}/custom-fields/${field.id}`;

    setLoading(true);
    try {
      await axios.put(putUrl, {
        text_value: null,
        file_path: null,
        file_paths: null,
        ...(areaType === 'purchases' ? { document_instance_id: null } : {}),
      });
      setFiles((prev) => ({ ...prev, [field.id]: null }));
      setPlatformDocumentSelection((prev) => ({ ...prev, [field.id]: null }));
      onSave();
      showAlert({
        title: 'Documento removido',
        message: 'O vínculo do documento foi removido com sucesso.',
        variant: 'success',
      });
    } catch (error: any) {
      showAlert({
        title: 'Erro ao remover documento',
        message: error?.response?.data?.detail || 'Não foi possível remover o documento deste campo.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const parseRawMultiFileEntries = (raw: string | null | undefined): Array<string | Record<string, unknown>> => {
    if (!raw || !String(raw).trim()) return [];
    try {
      const parsed = JSON.parse(String(raw).trim());
      if (Array.isArray(parsed)) {
        return parsed.filter((item) => typeof item === 'string' || (item && typeof item === 'object'));
      }
      if (typeof parsed === 'string' && parsed.trim()) return [parsed.trim()];
      if (parsed && typeof parsed === 'object') return [parsed as Record<string, unknown>];
      return [];
    } catch {
      return [String(raw).trim()];
    }
  };

  const extractEntryPath = (entry: string | Record<string, unknown>): string => {
    if (typeof entry === 'string') return entry;
    const pathCandidate = entry.path ?? entry.file_path;
    return typeof pathCandidate === 'string' ? pathCandidate : '';
  };

  const clearSingleStoredMultiFileEntry = async (
    field: WorkflowCustomField,
    existingValue: CustomFieldValue | undefined,
    targetEntry: ParsedCustomFieldFileEntry
  ) => {
    if (!canEdit) return;
    if (!existingValue?.file_paths) return;

    const rawEntries = parseRawMultiFileEntries(existingValue.file_paths);
    if (rawEntries.length === 0) return;

    const normalizedTargetPath = normalizeStoragePathForDownload(targetEntry.path || '');
    let visibleIndex = -1;
    let rawIndexToRemove = -1;

    rawEntries.forEach((entry, rawIndex) => {
      const entryPath = extractEntryPath(entry);
      if (!entryPath || !String(entryPath).trim()) return;
      visibleIndex += 1;
      const normalizedEntryPath = normalizeStoragePathForDownload(entryPath);
      if (rawIndexToRemove !== -1) return;
      if (visibleIndex === targetEntry.fileIndex && normalizedEntryPath === normalizedTargetPath) {
        rawIndexToRemove = rawIndex;
      }
    });

    if (rawIndexToRemove === -1) {
      showAlert({
        title: 'Arquivo não encontrado',
        message: 'Não foi possível identificar o anexo selecionado para remoção.',
        variant: 'warning',
      });
      return;
    }

    const remainingRawEntries = rawEntries.filter((_, idx) => idx !== rawIndexToRemove);
    const remainingVisibleEntries = remainingRawEntries.filter((entry) => {
      const p = extractEntryPath(entry);
      return !!p && String(p).trim().length > 0;
    });

    const putUrl = isOpportunitiesModule
      ? `${API_URL}/api/purchases/opportunities/module/custom-fields/${field.id}`
      : `${API_URL}/api/${areaType === 'purchases' ? 'purchases' : 'tickets'}/${ticketId}/custom-fields/${field.id}`;

    setLoading(true);
    try {
      await axios.put(putUrl, {
        file_paths: remainingVisibleEntries.length ? JSON.stringify(remainingRawEntries) : null,
        ...(remainingVisibleEntries.length ? {} : { file_path: null, text_value: null }),
      });
      onSave();
      showAlert({
        title: 'Anexo desvinculado',
        message: 'O anexo foi removido com sucesso deste campo.',
        variant: 'success',
      });
    } catch (error: any) {
      showAlert({
        title: 'Erro ao remover anexo',
        message: error?.response?.data?.detail || 'Não foi possível remover o anexo selecionado.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    setLoading(true);
    try {
      const saveErrors: string[] = [];
      // Campos reprovados na validação prévia não são gravados, mas também não podem
      // abortar o salvamento dos demais (senão o usuário perde tudo que digitou).
      const blockedFieldIds = new Set<number>();
      const fieldsForSubmit = orderFieldsForSave(conditionalContextFields);
      for (const field of fieldsForSubmit) {
        const isVisibleInStep =
          field.step_is_visible === undefined || field.step_is_visible === null
            ? true
            : !!field.step_is_visible;
        const isBlockedInStep =
          field.step_is_blocked === undefined || field.step_is_blocked === null
            ? false
            : !!field.step_is_blocked;
        const isVisibleByCondition = shouldShowField(field);
        const isFileTypeField = field.field_type === 'file' || field.field_type === 'files';

        if (!isVisibleInStep || isBlockedInStep || (!isVisibleByCondition && !isFileTypeField)) {
          continue;
        }
        if (!isSupplierCustomField(field)) {
          continue;
        }

        const existingValue = customFieldValues.find((v) => v.custom_field_id === field.id);
        const supplierRawValue = values[field.id] !== undefined
          ? values[field.id]
          : hydrateStoredFieldValue(field, existingValue);
        const supplierError = validateSupplierSelectionValue(
          typeof supplierRawValue === 'string' ? supplierRawValue : '',
          workflowName,
          field.options
        );
        if (supplierError) {
          const fieldLabel = field.label || field.name || `ID ${field.id}`;
          saveErrors.push(`${fieldLabel}: ${supplierError}`);
          blockedFieldIds.add(field.id);
          continue;
        }
      }

      // Processar cada campo
      for (const field of fieldsForSubmit) {
        if (blockedFieldIds.has(field.id)) {
          continue;
        }

        const isVisibleInStep =
          field.step_is_visible === undefined || field.step_is_visible === null
            ? true
            : !!field.step_is_visible;
        const isBlockedInStep =
          field.step_is_blocked === undefined || field.step_is_blocked === null
            ? false
            : !!field.step_is_blocked;

        // Respeitar configuração da etapa: campos ocultos ou bloqueados não devem ser alterados
        if (!isVisibleInStep || isBlockedInStep) {
          continue;
        }

        // Campos condicionais ocultos não devem ser alterados nem "limpos" no salvar.
        // Exceção: campos de arquivo podem aparecer sem condição ativa para permitir upload.
        const isFileTypeField = field.field_type === 'file' || field.field_type === 'files';
        if (!shouldShowField(field) && !isFileTypeField) {
          continue;
        }

        const fieldValue = values[field.id];
        const fieldFile = files[field.id];
        const existingPlatformValue =
          field.field_type === 'platform_document'
            ? customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id)
            : undefined;
        const existingValue = customFieldValues.find((v) => v.custom_field_id === field.id);
        // Valor local hidratado manda no salvamento; sem isso, limpar um campo nunca
        // persistiria, porque o valor antigo do banco seria regravado.
        const effectiveFieldValue = fieldValue !== undefined
          ? fieldValue
          : hydrateStoredFieldValue(field, existingValue);
        const existingPlatformPurchaseValue =
          existingPlatformValue && 'document_instance_id' in existingPlatformValue
            ? (existingPlatformValue as PurchaseCustomFieldValue)
            : undefined;
        const effectivePlatformDocumentMode =
          field.field_type === 'platform_document'
            ? (
                field.document_type_id
                  ? 'platform'
                  : (
                      platformDocumentMode[field.id] ||
                      (existingPlatformPurchaseValue?.document_instance_id ? 'platform' : 'upload')
                    )
              )
            : undefined;
        const isPlatformDocumentUpload =
          field.field_type === 'platform_document' &&
          effectivePlatformDocumentMode === 'upload';
        const isTechnicalProposalUpload =
          field.field_type === 'technical_proposal_review' &&
          (effectiveFieldValue as TechnicalProposalReviewValue | undefined)?.proposal_source === 'upload';

        try {
          if (field.field_type === 'file' || field.field_type === 'files' || isPlatformDocumentUpload || isTechnicalProposalUpload) {
            if (isOpportunitiesModule && !isTechnicalProposalUpload) {
              continue;
            }
            // Upload de arquivo
            if (fieldFile) {
              if (field.field_type === 'file' || isPlatformDocumentUpload || isTechnicalProposalUpload) {
                // Arquivo único (inclui platform_document no modo upload)
                const formData = new FormData();
                formData.append('file', fieldFile as File);
                
                await axios.post(
                  `${API_URL}/api/${areaType === 'purchases' ? 'purchases' : 'tickets'}/${ticketId}/custom-fields/${field.id}/upload`,
                  formData,
                  {
                    headers: { 'Content-Type': 'multipart/form-data' },
                  }
                );
              } else {
                // Múltiplos arquivos
                const formData = new FormData();
                if (Array.isArray(fieldFile)) {
                  fieldFile.forEach((f) => {
                    formData.append('files', f);
                  });
                } else {
                  formData.append('files', fieldFile as File);
                }
                
                await axios.post(
                  `${API_URL}/api/${areaType === 'purchases' ? 'purchases' : 'tickets'}/${ticketId}/custom-fields/${field.id}/upload-multiple`,
                  formData,
                  {
                    headers: { 'Content-Type': 'multipart/form-data' },
                  }
                );
              }
            }
          } else {
            // platform_document é salvo por upload (endpoint /upload) ou por seleção explícita
            // no próprio controle (PUT/POST imediato ao trocar documento). Evita PUT em massa
            // durante "Salvar Campos", que pode revalidar dados legados e bloquear alterações
            // de outros campos não relacionados.
            if (field.field_type === 'platform_document') {
              continue;
            }

            // Campo de texto ou seleção
            const technicalProposalValue =
              field.field_type === 'technical_proposal_review'
                ? (effectiveFieldValue as TechnicalProposalReviewValue | undefined)
                : undefined;

            const normalizedTechnicalValue =
              technicalProposalValue && field.field_type === 'technical_proposal_review'
                ? {
                    decision: technicalProposalValue.decision,
                    opinion: (technicalProposalValue.opinion || '').trim(),
                    proposal_source: technicalProposalValue.proposal_source,
                    ...(technicalProposalValue.proposal_source === 'existing_attachment' &&
                    technicalProposalValue.purchase_attachment_id
                      ? { purchase_attachment_id: Number(technicalProposalValue.purchase_attachment_id) }
                      : {}),
                  }
                : undefined;

            const valueData: any = {
              ...(!isOpportunitiesModule
                ? areaType === 'purchases'
                  ? { purchase_request_id: ticketId }
                  : { ticket_id: ticketId }
                : {}),
              custom_field_id: field.id,
              text_value:
                field.field_type === 'table'
                  ? serializeTableValue(effectiveFieldValue)
                  : field.field_type === 'technical_proposal_review'
                    ? JSON.stringify(normalizedTechnicalValue || {})
                    : field.field_type === 'check'
                      ? (serializeCheckFieldValue(field, effectiveFieldValue) ?? '')
                    : (field.field_type === 'list' || field.field_type === 'combo') && !field.allow_multiple
                      ? normalizeSingleChoiceValue(effectiveFieldValue)
                    : (field.allow_multiple && (field.field_type === 'list' || field.field_type === 'combo')) && Array.isArray(effectiveFieldValue)
                      ? JSON.stringify(effectiveFieldValue)
                    : (effectiveFieldValue ?? ''),
            };

            // O upsert reconcilia divergências entre a lista carregada no cliente e o estado
            // real da API (valores podem ter sido removidos no servidor), evitando que um
            // 404/409 isolado descarte silenciosamente a alteração do campo.
            await upsertCustomFieldValue(API_URL, upsertArea, ticketId, valueData, {
              knownExists: !!existingValue,
            });
          }
        } catch (fieldError: any) {
          const fieldLabel = field.label || field.name || `ID ${field.id}`;
          const detail = fieldError?.response?.data?.detail;
          const detailText =
            typeof detail === 'string'
              ? detail
              : detail
                ? JSON.stringify(detail)
                : (fieldError?.message || 'Erro desconhecido');
          saveErrors.push(`${fieldLabel}: ${detailText}`);
          continue;
        }
      }

      if (saveErrors.length > 0) {
        showAlert({
          title: 'Salvar concluído com pendências',
          message: `Alguns campos não puderam ser salvos (${saveErrors.length}).`,
          variant: 'warning',
          details: saveErrors.join('\n'),
        });
      } else {
        showAlert({
          title: 'Sucesso',
          message: 'Campos customizados salvos com sucesso!',
          variant: 'success'
        });
      }
      
      // Recarregar dados após um pequeno delay para evitar conflitos com o modal
      setTimeout(() => {
        onSave();
      }, 100);
    } catch (error: any) {
      console.error('Erro ao salvar campos customizados:', error);
      let errorDetails = 'Erro desconhecido';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Converter objeto de erro em string se necessário
        if (typeof detail === 'object') {
          if (Array.isArray(detail)) {
            errorDetails = detail.map((d: any) => {
              if (typeof d === 'object' && d.msg) {
                return `${d.loc?.join('.') || ''}: ${d.msg}`;
              }
              return String(d);
            }).join('\n');
          } else {
            errorDetails = JSON.stringify(detail);
          }
        } else {
          errorDetails = String(detail);
        }
      }
      showAlert({
        title: 'Erro ao Salvar',
        message: 'Não foi possível salvar os campos customizados.',
        variant: 'danger',
        details: errorDetails
      });
    } finally {
      setLoading(false);
    }
  };

  const parseOptions = (options?: string): string[] => {
    if (!options) return [];
    // Tentar parsear como JSON primeiro
    try {
      const parsed = JSON.parse(options);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      // Se não for JSON, tratar como texto separado por vírgula ou quebra de linha
      return options
        .split(/[,\n]/)
        .map((opt) => opt.trim())
        .filter((opt) => opt.length > 0);
    }
    return [];
  };

  const shouldShowField = (field: WorkflowCustomField): boolean =>
    shouldShowConditionalField(field, conditionalContextFields, conditionalValues);

  const hasStoredValue = (fieldId: number): boolean => {
    const field = customFields.find((f) => f.id === fieldId);
    const existingValue = customFieldValues.find((v) => v.custom_field_id === fieldId);
    if (!existingValue) return false;

    const hasTextValue = field?.field_type === 'check'
      ? hasCheckFieldPersistedValue(field, existingValue.text_value)
      : (
          existingValue.text_value !== undefined &&
          existingValue.text_value !== null &&
          String(existingValue.text_value).trim() !== ''
        );

    // Verificar document_instance_id apenas para PurchaseCustomFieldValue
    const hasDocumentInstance = 'document_instance_id' in existingValue && 
      existingValue.document_instance_id !== undefined && 
      existingValue.document_instance_id !== null;

    return Boolean(
      hasTextValue ||
      existingValue.file_path ||
      existingValue.file_paths ||
      hasDocumentInstance
    );
  };

  const renderField = (field: WorkflowCustomField) => {
    const fieldValue = values[field.id];
    const fieldFile = files[field.id];
    const existingValue = customFieldValues.find((v) => v.custom_field_id === field.id);
    // Depois de hidratado, o estado local é a fonte de verdade. Cair de volta no valor do
    // servidor quando o local está vazio faria um campo limpo ou desmarcado voltar sozinho.
    const hasLocalValue = fieldValue !== undefined;
    const displayValue = hasLocalValue ? fieldValue : (existingValue?.text_value ?? fieldValue);
    const resolveStoredTextValue = (): string =>
      String((hasLocalValue ? fieldValue : existingValue?.text_value) ?? '').trim();
    const isRequired = !!field.is_required || !!field.step_is_required;
    // step_is_visible/step_is_blocked podem vir null quando a etapa não tem configuração explícita
    const isBlockedInStep = field.step_is_blocked === true;
    const isHiddenInStep = field.step_is_visible === false;
    const isBlocked = isBlockedInStep || isHiddenInStep;
    const isSupplierField = isSupplierCustomField(field);

    if (isSupplierField) {
      return (
        <SupplierFieldControl
          value={typeof fieldValue === 'string' ? fieldValue : (existingValue?.text_value || '')}
          onChange={(nextSerializedValue) => handleTextChange(field.id, nextSerializedValue)}
          disabled={!canEdit || isBlocked}
          required={isRequired}
          workflowName={workflowName}
          fieldOptions={field.options}
        />
      );
    }

    switch (field.field_type) {
      case 'text_short':
        return (
          <Form.Control
            type="text"
            value={displayValue || ''}
            onChange={(e) => handleTextChange(field.id, e.target.value)}
            disabled={!canEdit || isBlocked}
            required={isRequired}
            readOnly={isBlocked}
          />
        );

      case 'text_long':
        return (
          <Form.Control
            as="textarea"
            rows={4}
            value={displayValue || ''}
            onChange={(e) => handleTextChange(field.id, e.target.value)}
            disabled={!canEdit || isBlocked}
            required={isRequired}
            readOnly={isBlocked}
          />
        );

      case 'list':
      case 'combo': {
        const options = parseOptions(field.options);
        // Garantir que sempre mostramos o campo, mesmo com apenas 1 opção
        if (options.length === 0) {
          return (
            <div className="text-muted">
              <small>Nenhuma opção configurada para este campo</small>
            </div>
          );
        }
        
        if (field.allow_multiple) {
          const selectedValues = Array.isArray(displayValue) ? displayValue : [];
          return (
            <div>
              {options.map((option, idx) => (
                <Form.Check
                  key={idx}
                  type="checkbox"
                  label={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter((v) => v !== option);
                    handleSelectChange(field.id, newValues);
                  }}
                  disabled={!canEdit || isBlocked}
                />
              ))}
            </div>
          );
        } else {
          const singleValue = resolveSingleChoiceOptionValue(options, displayValue);
          return (
            <Form.Select
              value={singleValue}
              onChange={(e) => handleSelectChange(field.id, e.target.value)}
              disabled={!canEdit || isBlocked}
              required={isRequired}
            >
              <option value="">Selecione...</option>
              {options.map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          );
        }
      }

      case 'file': {
        const currentDisplayName = getSingleCustomFieldFileDisplayName(
          existingValue?.file_path,
          existingValue?.text_value
        );
        return (
          <div>
            {existingValue?.file_path && (
              <div className="mb-2">
                <button
                  type="button"
                  onClick={() => {
                    if (existingValue?.file_path) {
                      downloadFile(existingValue.file_path, currentDisplayName, {
                        onSessionExpired: () => showAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                        onError: (msg) => showAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                        onFileMissing: () =>
                          showAlert({
                            title: 'Arquivo indisponível',
                            message: 'Este ficheiro não foi encontrado no armazenamento.',
                            variant: 'warning',
                          }),
                      });
                    }
                  }}
                  className="btn btn-sm btn-outline-primary me-2"
                >
                  Ver arquivo atual
                </button>
                {canEdit && !isBlocked && (
                  <button
                    type="button"
                    onClick={() => clearStoredFileValue(field)}
                    className="btn btn-sm btn-outline-danger"
                  >
                    Remover arquivo
                  </button>
                )}
                {existingValue?.file_missing && (
                  <Badge bg="warning" text="dark" className="me-2">
                    Indisponível
                  </Badge>
                )}
                <div className="small text-muted text-break mt-1">{currentDisplayName}</div>
              </div>
            )}
            <Form.Control
              type="file"
              onChange={(e) => {
                const target = e.target as HTMLInputElement;
                handleFileChange(field.id, target.files?.[0] || null);
              }}
              disabled={!canEdit || isBlocked}
              accept={ATTACHMENT_ACCEPT}
            />
            {fieldFile && (
              <small className="text-muted d-block mt-1">
                Arquivo selecionado: {(fieldFile as File).name}
              </small>
            )}
          </div>
        );
      }

      case 'files':
        return (
          <div>
            {existingValue?.file_paths && (
              <div className="mb-2">
                {parseCustomFieldFilePathsJson(existingValue.file_paths).map((entry, idx) => (
                  <div key={idx} className="custom-files-entry">
                    <div className="custom-files-entry-title text-break">
                      <span className="custom-files-entry-name">{entry.displayName}</span>
                      {entry.fileMissing && (
                        <Badge bg="warning" text="dark" className="ms-2">
                          Indisponível
                        </Badge>
                      )}
                    </div>
                    <div className="custom-files-entry-actions d-flex align-items-center flex-wrap gap-3">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 custom-files-action-link"
                        type="button"
                        onClick={() =>
                          openFileInNewTab(entry.path, {
                            onSessionExpired: () =>
                              showAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                            onError: (msg) => showAlert({ title: 'Erro ao abrir arquivo', message: msg, variant: 'danger' }),
                            onFileMissing: () =>
                              showAlert({
                                title: 'Arquivo indisponível',
                                message: 'Este ficheiro não foi encontrado no armazenamento.',
                                variant: 'warning',
                              }),
                          })
                        }
                      >
                        Abrir anexo em nova aba
                      </Button>
                      {canEdit && !isBlocked && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 custom-files-action-link custom-files-action-link-danger"
                          type="button"
                          onClick={() => clearSingleStoredMultiFileEntry(field, existingValue, entry)}
                          disabled={loading}
                        >
                          Remover vínculo
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Form.Control
              type="file"
              multiple
              onChange={(e) => {
                const target = e.target as HTMLInputElement;
                handleFileChange(field.id, target.files);
              }}
              disabled={!canEdit || isBlocked}
              accept={ATTACHMENT_ACCEPT}
            />
            {fieldFile && Array.isArray(fieldFile) && (
              <small className="text-muted d-block mt-1">
                {fieldFile.length} arquivo(s) selecionado(s)
              </small>
            )}
          </div>
        );

      case 'technical_proposal_review': {
        const proposalValue = (fieldValue as TechnicalProposalReviewValue | undefined) || {
          decision: 'approved',
          opinion: '',
          proposal_source: existingValue?.file_path ? 'upload' : 'existing_attachment',
        };
        const availablePdfAttachments = purchaseAttachments.filter((attachment) => {
          const fileName = (attachment.filename || '').toLowerCase();
          const mimeType = (attachment.mime_type || '').toLowerCase();
          return fileName.endsWith('.pdf') || mimeType.includes('pdf');
        });
        const selectedAttachment = availablePdfAttachments.find(
          (attachment) => attachment.id === proposalValue.purchase_attachment_id
        );
        const currentSource = proposalValue.proposal_source || 'upload';

        const copyCommentLink = async () => {
          const rawUrl = currentSource === 'existing_attachment'
            ? selectedAttachment?.file_path
            : existingValue?.file_path;
          if (!rawUrl) {
            showAlert({ title: 'Aviso', message: 'Nenhum arquivo disponível para gerar link.', variant: 'warning' });
            return;
          }
          const url = rawUrl.startsWith('http://') || rawUrl.startsWith('https://')
            ? rawUrl
            : rawUrl.startsWith('/')
              ? `${API_URL.replace(/\/$/, '')}${rawUrl}`
              : `${API_URL.replace(/\/$/, '')}/${rawUrl.replace(/^\//, '')}`;
          const label = currentSource === 'existing_attachment'
            ? (selectedAttachment?.filename || 'Proposta técnica')
            : getSingleCustomFieldFileDisplayName(existingValue?.file_path, existingValue?.text_value);
          try {
            await navigator.clipboard.writeText(url);
            showAlert({
              title: 'Link copiado',
              message: `URL de "${label}" copiada. Cole no comentário para gerar o botão de link.`,
              variant: 'success'
            });
          } catch {
            showAlert({
              title: 'Falha ao copiar',
              message: 'Não foi possível copiar automaticamente. Copie o link do arquivo manualmente.',
              variant: 'warning'
            });
          }
        };

        return (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>Origem da proposta (PDF)</Form.Label>
              <Form.Check
                type="radio"
                id={`proposal-source-upload-${field.id}`}
                name={`proposal-source-${field.id}`}
                label="Enviar PDF manualmente"
                checked={currentSource === 'upload'}
                disabled={!canEdit || isBlocked}
                onChange={() =>
                  setValues(prevValues => ({
                    ...prevValues,
                    [field.id]: {
                      ...proposalValue,
                      proposal_source: 'upload',
                      purchase_attachment_id: undefined,
                    },
                  }))
                }
              />
              <Form.Check
                type="radio"
                id={`proposal-source-existing-${field.id}`}
                name={`proposal-source-${field.id}`}
                label="Referenciar anexo PDF já existente na requisição"
                checked={currentSource === 'existing_attachment'}
                disabled={!canEdit || isBlocked}
                onChange={() =>
                  setValues(prevValues => ({
                    ...prevValues,
                    [field.id]: {
                      ...proposalValue,
                      proposal_source: 'existing_attachment',
                    },
                  }))
                }
              />
            </Form.Group>

            {currentSource === 'upload' ? (
              <Form.Group className="mb-3">
                {existingValue?.file_path && (
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (existingValue?.file_path) {
                          downloadFile(existingValue.file_path, getSingleCustomFieldFileDisplayName(existingValue.file_path, existingValue.text_value), {
                            onSessionExpired: () => showAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                            onError: (msg) => showAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                            onFileMissing: () =>
                              showAlert({
                                title: 'Arquivo indisponível',
                                message: 'Este ficheiro não foi encontrado no armazenamento.',
                                variant: 'warning',
                              }),
                          });
                        }
                      }}
                      className="btn btn-sm btn-outline-primary me-2"
                    >
                      Ver PDF atual
                    </button>
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const target = e.target as HTMLInputElement;
                    handleFileChange(field.id, target.files?.[0] || null);
                  }}
                  disabled={!canEdit || isBlocked}
                  required={isRequired}
                />
                {fieldFile && (
                  <small className="text-muted d-block mt-1">
                    Arquivo selecionado: {(fieldFile as File).name}
                  </small>
                )}
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Anexo PDF existente</Form.Label>
                <Form.Select
                  value={proposalValue.purchase_attachment_id || ''}
                  disabled={!canEdit || isBlocked}
                  required={isRequired}
                  onChange={(e) =>
                    setValues(prevValues => ({
                      ...prevValues,
                      [field.id]: {
                        ...proposalValue,
                        proposal_source: 'existing_attachment',
                        purchase_attachment_id: e.target.value ? Number(e.target.value) : undefined,
                      },
                    }))
                  }
                >
                  <option value="">Selecione um anexo PDF...</option>
                  {availablePdfAttachments.map((attachment) => (
                    <option key={attachment.id} value={attachment.id}>
                      {attachment.filename}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Decisão da análise técnica</Form.Label>
              <Form.Select
                value={proposalValue.decision || 'approved'}
                disabled={!canEdit || isBlocked}
                required={isRequired}
                onChange={(e) =>
                  setValues(prevValues => ({
                    ...prevValues,
                    [field.id]: {
                      ...proposalValue,
                      decision: e.target.value as TechnicalProposalDecision,
                    },
                  }))
                }
              >
                <option value="approved">Aprovada</option>
                <option value="clarifications_for_approval">Esclarecimentos complementares para aprovação</option>
                <option value="rejected">Reprovada</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Parecer técnico</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={proposalValue.opinion || ''}
                disabled={!canEdit || isBlocked}
                required={isRequired}
                onChange={(e) =>
                  setValues(prevValues => ({
                    ...prevValues,
                    [field.id]: {
                      ...proposalValue,
                      opinion: e.target.value,
                    },
                  }))
                }
                placeholder="Descreva o parecer técnico da proposta."
              />
            </Form.Group>

            <Button
              type="button"
              size="sm"
              variant="outline-secondary"
              onClick={copyCommentLink}
              disabled={isBlocked || (!existingValue?.file_path && !selectedAttachment?.file_path)}
            >
              Copiar link HTML para comentário
            </Button>
          </div>
        );
      }

      case 'platform_document': {
        if (areaType !== 'purchases') {
          return <Alert variant="warning">Este tipo de campo só está disponível para compras.</Alert>;
        }

        const existingValue = customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id);
        const purchaseValue = existingValue && 'document_instance_id' in existingValue ? existingValue as PurchaseCustomFieldValue : undefined;
        const memorialRestricted = Boolean(field.document_type_id);
        const currentMode = memorialRestricted
          ? 'platform'
          : platformDocumentMode[field.id] ||
            (purchaseValue?.document_instance_id ? 'platform' : existingValue?.file_path ? 'upload' : 'upload');
        const selectedDocumentId = platformDocumentSelection[field.id] ?? purchaseValue?.document_instance_id ?? null;
        const availablePlatformDocs = filterPlatformDocumentsForField(field, documentInstances);
        // O documento já vinculado pode não constar em /available (criado por outro usuário,
        // fora do tipo configurado ou lista ainda carregando). Sem uma opção correspondente,
        // o select fica sem valor e o navegador bloqueia o salvamento pedindo para
        // "selecionar um item na lista", mesmo sem nenhuma alteração no campo.
        const linkedDocumentMissingFromList =
          !!selectedDocumentId && !availablePlatformDocs.some((doc) => doc.id === selectedDocumentId);
        const linkedDocumentLabel = purchaseValue?.document_instance
          ? formatPlatformDocumentLabel(purchaseValue.document_instance)
          : `Documento vinculado #${selectedDocumentId}`;

        return (
          <div>
            {!memorialRestricted && (
            <Form.Group className="mb-3">
              <Form.Label>Modo de Seleção</Form.Label>
              <Form.Select
                value={currentMode}
                onChange={(e) => {
                  const newMode = e.target.value as 'upload' | 'platform';
                  setPlatformDocumentMode({ ...platformDocumentMode, [field.id]: newMode });
                  if (newMode === 'upload') {
                    handleTextChange(field.id, '');
                  } else {
                    setFiles({ ...files, [field.id]: null });
                  }
                }}
                disabled={!canEdit || isBlocked}
              >
                <option value="upload">Upload de Arquivo</option>
                <option value="platform">Documento Criado na Plataforma</option>
              </Form.Select>
            </Form.Group>
            )}

            {memorialRestricted && (
              <p className="text-muted small mb-2">
                Selecione um documento finalizado criado em Documentos.
              </p>
            )}

            {currentMode === 'upload' ? (
              <div>
                {existingValue?.file_path && (
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (existingValue?.file_path) {
                          const dn = getSingleCustomFieldFileDisplayName(
                            existingValue.file_path,
                            existingValue.text_value
                          );
                          downloadFile(existingValue.file_path, dn, {
                            onSessionExpired: () => showAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                            onError: (msg) => showAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                            onFileMissing: () =>
                              showAlert({
                                title: 'Arquivo indisponível',
                                message: 'Este ficheiro não foi encontrado no armazenamento.',
                                variant: 'warning',
                              }),
                          });
                        }
                      }}
                      className="btn btn-sm btn-outline-primary me-2"
                    >
                      Ver arquivo atual
                    </button>
                    {canEdit && !isBlocked && (
                      <button
                        type="button"
                        onClick={() => clearStoredFileValue(field)}
                        className="btn btn-sm btn-outline-danger"
                      >
                        Remover arquivo
                      </button>
                    )}
                    {existingValue?.file_missing && (
                      <Badge bg="warning" text="dark" className="me-2">
                        Indisponível
                      </Badge>
                    )}
                    <div className="small text-muted text-break">
                      {getSingleCustomFieldFileDisplayName(existingValue.file_path, existingValue.text_value)}
                    </div>
                  </div>
                )}
                <Form.Control
                  type="file"
                  onChange={(e) => {
                    const target = e.target as HTMLInputElement;
                    handleFileChange(field.id, target.files?.[0] || null);
                  }}
                  disabled={!canEdit || isBlocked}
                  accept={ATTACHMENT_ACCEPT}
                />
                {files[field.id] && (
                  <small className="text-muted d-block mt-1">
                    Arquivo selecionado: {(files[field.id] as File).name}
                  </small>
                )}
              </div>
            ) : (
              <div>
                <Form.Select
                  value={selectedDocumentId || ''}
                  onChange={async (e) => {
                    const docId = e.target.value ? parseInt(e.target.value, 10) : null;
                    const previousDocId = selectedDocumentId;
                    setPlatformDocumentSelection((prev) => ({ ...prev, [field.id]: docId }));

                    if (!docId) {
                      if (previousDocId) {
                        await clearStoredFileValue(field);
                      }
                      return;
                    }

                    const value = customFieldValues.find((v) => (v as CustomFieldValue).custom_field_id === field.id);
                    const valueData: any = {
                      document_instance_id: docId,
                      text_value: null,
                      file_path: null,
                      file_paths: null
                    };

                    setSavingPlatformDocument((prev) => ({ ...prev, [field.id]: true }));
                    try {
                      await upsertCustomFieldValue(
                        API_URL,
                        upsertArea,
                        ticketId,
                        {
                          ...valueData,
                          custom_field_id: field.id,
                          ...(upsertArea === 'purchases' ? { purchase_request_id: ticketId } : {}),
                          ...(upsertArea === 'tickets' ? { ticket_id: ticketId } : {}),
                        },
                        { knownExists: !!value }
                      );
                      onSave();
                    } catch (error: any) {
                      console.error('Erro ao salvar documento da plataforma:', error);
                      setPlatformDocumentSelection((prev) => ({ ...prev, [field.id]: previousDocId }));
                      showAlert({
                        title: 'Erro ao selecionar documento',
                        message: error?.response?.data?.detail || 'Não foi possível vincular o documento selecionado.',
                        variant: 'danger'
                      });
                    } finally {
                      setSavingPlatformDocument((prev) => ({ ...prev, [field.id]: false }));
                    }
                  }}
                  disabled={!canEdit || isBlocked || !!savingPlatformDocument[field.id]}
                  required={isRequired}
                >
                  <option value="">Selecione um documento...</option>
                  {linkedDocumentMissingFromList && (
                    <option value={String(selectedDocumentId)}>{linkedDocumentLabel}</option>
                  )}
                  {availablePlatformDocs.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {formatPlatformDocumentLabel(doc)}
                    </option>
                  ))}
                </Form.Select>
                {selectedDocumentId && (
                  <div className="d-flex gap-3 align-items-center mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => window.open(`/documents/${selectedDocumentId}/edit`, '_blank')}
                    >
                      Abrir documento em nova aba
                    </Button>
                    {canEdit && !isBlocked && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={() => clearStoredFileValue(field)}
                      >
                        Remover vínculo
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case 'date':
        return (
          <Form.Control
            type="date"
            value={displayValue || ''}
            onChange={(e) => handleTextChange(field.id, e.target.value)}
            disabled={!canEdit || isBlocked}
            required={isRequired}
            readOnly={isBlocked}
          />
        );

      case 'period': {
        let periodValue = { start: '', end: '' };
        if (fieldValue) {
          try {
            periodValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
          } catch {
            // Se não for JSON válido, tentar parsear como string simples
            periodValue = { start: fieldValue || '', end: '' };
          }
        }
        return (
          <div className="d-flex gap-2">
            <Form.Control
              type="date"
              placeholder="Data Início"
              value={periodValue.start || ''}
              onChange={(e) => {
                const newPeriod = { ...periodValue, start: e.target.value };
                handleTextChange(field.id, JSON.stringify(newPeriod));
              }}
              disabled={!canEdit || isBlocked}
              required={isRequired}
              readOnly={isBlocked}
            />
            <Form.Control
              type="date"
              placeholder="Data Fim"
              value={periodValue.end || ''}
              onChange={(e) => {
                const newPeriod = { ...periodValue, end: e.target.value };
                handleTextChange(field.id, JSON.stringify(newPeriod));
              }}
              disabled={!canEdit || isBlocked}
              required={isRequired}
              readOnly={isBlocked}
            />
          </div>
        );
      }

      case 'link':
        return (
          <Form.Control
            type="url"
            value={displayValue || ''}
            onChange={(e) => handleTextChange(field.id, e.target.value)}
            disabled={!canEdit || isBlocked}
            required={isRequired}
            readOnly={isBlocked}
            placeholder="https://example.com"
          />
        );

      case 'user': {
        const userSearchTerm = searchUsers[field.id] || '';
        const filteredUserList = userSearchTerm
          ? users.filter(u => 
              u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
              u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
            ).slice(0, 10)
          : [];
        const userOptionsToShow = userSearchTerm ? filteredUserList : (filteredUsers[field.id] || []);
        const rawUserValue = resolveStoredTextValue();
        const selectedUserId = parseInt(rawUserValue || '0', 10);
        const selectedUser = users.find(u => u.id === selectedUserId);
        const selectedUserLabel = selectedUser
          ? `${selectedUser.name} (${selectedUser.email})`
          : '';
        
        return (
          <div style={{ position: 'relative' }}>
            <Form.Control
              type="text"
              value={
                userSearchTerm !== ''
                  ? userSearchTerm
                  : (selectedUserLabel || rawUserValue)
              }
              onChange={(e) => {
                const value = e.target.value;
                setSearchUsers({ ...searchUsers, [field.id]: value });
                // Se o usuário apagar o texto, limpar o valor selecionado
                if (!value) {
                  handleTextChange(field.id, '');
                } else if (selectedUser && !value.includes(selectedUser.name || '') && !value.includes(selectedUser.email || '')) {
                  // Se o texto não corresponde mais ao usuário selecionado, limpar
                  handleTextChange(field.id, '');
                }
              }}
              onBlur={() => {
                // Validar ao perder o foco: se não há usuário selecionado e há texto, limpar
                setTimeout(() => {
                  if (!selectedUser && userSearchTerm) {
                    // Verificar se o texto corresponde a algum usuário
                    const matchingUser = users.find(u => 
                      userSearchTerm.toLowerCase() === `${u.name} (${u.email})`.toLowerCase() ||
                      userSearchTerm.toLowerCase() === u.name?.toLowerCase() ||
                      userSearchTerm.toLowerCase() === u.email?.toLowerCase()
                    );
                    if (!matchingUser) {
                      // Se não corresponde, limpar
                      setSearchUsers({ ...searchUsers, [field.id]: '' });
                      handleTextChange(field.id, '');
                    }
                  }
                  setFilteredUsers((prev) => ({ ...prev, [field.id]: [] }));
                }, 120);
              }}
              onFocus={() => {
                if (!userSearchTerm) {
                  setFilteredUsers({ ...filteredUsers, [field.id]: users.slice(0, 10) });
                }
              }}
              disabled={!canEdit || isBlocked}
              required={isRequired}
              readOnly={isBlocked}
              placeholder="Digite o nome ou email do usuário"
            />
            {userOptionsToShow.length > 0 && !selectedUser && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ced4da',
                borderRadius: '0 0 0.375rem 0.375rem',
                maxHeight: '200px',
                overflowY: 'auto',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
                {userOptionsToShow.map((user) => (
                  <div
                    key={user.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleTextChange(field.id, user.id.toString());
                      setSearchUsers({ ...searchUsers, [field.id]: '' });
                      setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                    }}
                    onClick={() => {
                      handleTextChange(field.id, user.id.toString());
                      setSearchUsers({ ...searchUsers, [field.id]: '' });
                      setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #e9ecef',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    {user.name} ({user.email})
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        }

      case 'approver':
      case 'contract_manager_approver': {
        const approverSearchTerm = searchUsers[field.id] || '';
        const approverBaseList = (approverUsers.length > 0 ? approverUsers : users).filter(
          (u) =>
            u.is_active &&
            (field.field_type === 'contract_manager_approver' || !!u.can_approve)
        );
        const filteredApproverList = approverSearchTerm
          ? approverBaseList.filter(u => 
              u.name?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
              u.email?.toLowerCase().includes(approverSearchTerm.toLowerCase())
            ).slice(0, 10)
          : [];
        const approverOptionsToShow = approverSearchTerm ? filteredApproverList : (filteredUsers[field.id] || []);
        const rawApproverValue = resolveStoredTextValue();
        const selectedApproverId = parseInt(rawApproverValue || '0', 10);
        const selectedApprover = approverBaseList.find(u => u.id === selectedApproverId);
        const selectedApproverLabel = selectedApprover
          ? `${selectedApprover.name} (${selectedApprover.email})`
          : '';
        
        return (
          <div style={{ position: 'relative' }}>
            <Form.Control
              type="text"
              value={
                approverSearchTerm !== ''
                  ? approverSearchTerm
                  : (selectedApproverLabel || rawApproverValue)
              }
              onChange={(e) => {
                const value = e.target.value;
                setSearchUsers({ ...searchUsers, [field.id]: value });
                if (!value) {
                  handleTextChange(field.id, '');
                } else if (selectedApprover && value !== selectedApproverLabel) {
                  handleTextChange(field.id, '');
                }
              }}
              onBlur={() => {
                setTimeout(() => {
                  if (!selectedApprover && approverSearchTerm) {
                    const matchingApprover = approverBaseList.find(u => 
                      approverSearchTerm.toLowerCase() === `${u.name} (${u.email})`.toLowerCase() ||
                      approverSearchTerm.toLowerCase() === u.name?.toLowerCase() ||
                      approverSearchTerm.toLowerCase() === u.email?.toLowerCase()
                    );
                    if (!matchingApprover) {
                      setSearchUsers({ ...searchUsers, [field.id]: '' });
                      handleTextChange(field.id, '');
                    }
                  }
                  setFilteredUsers((prev) => ({ ...prev, [field.id]: [] }));
                }, 120);
              }}
              onFocus={() => {
                if (!approverSearchTerm) {
                  setFilteredUsers({ ...filteredUsers, [field.id]: approverBaseList.slice(0, 10) });
                }
              }}
              disabled={!canEdit || isBlocked}
              required={isRequired}
              readOnly={isBlocked}
              placeholder="Digite o nome ou email do aprovador"
            />
            {approverOptionsToShow.length > 0 && (!selectedApprover || approverSearchTerm !== '') && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ced4da',
                borderRadius: '0 0 0.375rem 0.375rem',
                maxHeight: '200px',
                overflowY: 'auto',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
              {approverOptionsToShow.map((user) => (
                <div
                  key={user.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleTextChange(field.id, user.id.toString());
                    setSearchUsers({ ...searchUsers, [field.id]: '' });
                    setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                  }}
                  onClick={() => {
                    handleTextChange(field.id, user.id.toString());
                    setSearchUsers({ ...searchUsers, [field.id]: '' });
                    setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #e9ecef',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {user.name} ({user.email})
                </div>
              ))}
            </div>
          )}
        </div>
        );
      }

      case 'analyst': {
        const analystSearchTerm = searchUsers[field.id] || '';
        const filteredAnalystList = analystSearchTerm
          ? analystUsers.filter(u => 
              u.name?.toLowerCase().includes(analystSearchTerm.toLowerCase()) ||
              u.email?.toLowerCase().includes(analystSearchTerm.toLowerCase())
            ).slice(0, 10)
          : [];
        const rawAnalystValue = resolveStoredTextValue();
        const selectedAnalystId = parseInt(rawAnalystValue || '0', 10);
        const selectedAnalyst = analystUsers.find(u => u.id === selectedAnalystId);
        const selectedAnalystLabel = selectedAnalyst
          ? `${selectedAnalyst.name} (${selectedAnalyst.email})`
          : '';
        
        return (
          <div style={{ position: 'relative' }}>
            {assignedGroupId ? (
              <>
                <Form.Control
                  type="text"
                  value={
                    analystSearchTerm !== ''
                      ? analystSearchTerm
                      : (selectedAnalystLabel || rawAnalystValue)
                  }
                  onChange={(e) => {
                    setSearchUsers({ ...searchUsers, [field.id]: e.target.value });
                    if (!e.target.value) {
                      handleTextChange(field.id, '');
                    }
                  }}
                  onFocus={() => {
                    if (!analystSearchTerm) {
                      setFilteredUsers({ ...filteredUsers, [field.id]: analystUsers.slice(0, 10) });
                    }
                  }}
                  disabled={!canEdit || isBlocked}
                  required={isRequired}
                  readOnly={isBlocked}
                  placeholder="Digite o nome ou email do analista"
                />
                {analystSearchTerm && filteredAnalystList.length > 0 && !selectedAnalyst && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderRadius: '0 0 0.375rem 0.375rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}>
                    {filteredAnalystList.map((analyst) => (
                      <div
                        key={analyst.id}
                        onClick={() => {
                          handleTextChange(field.id, analyst.id.toString());
                          setSearchUsers({ ...searchUsers, [field.id]: '' });
                          setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #e9ecef',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        {analyst.name} ({analyst.email})
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted">
                <small>Nenhum grupo atribuído ao ticket</small>
              </div>
            )}
          </div>
        );
      }

      case 'group': {
        const groupSearchTerm = searchGroups[field.id] || '';
        const filteredGroupList = groupSearchTerm
          ? groups.filter(g => 
              g.name?.toLowerCase().includes(groupSearchTerm.toLowerCase())
            ).slice(0, 10)
          : [];
        const rawGroupValue = resolveStoredTextValue();
        const selectedGroupId = parseInt(rawGroupValue || '0', 10);
        const selectedGroup = groups.find(g => g.id === selectedGroupId);
        
        return (
          <div style={{ position: 'relative' }}>
            <Form.Control
              type="text"
              value={
                groupSearchTerm !== ''
                  ? groupSearchTerm
                  : (selectedGroup ? selectedGroup.name : rawGroupValue)
              }
              onChange={(e) => {
                setSearchGroups({ ...searchGroups, [field.id]: e.target.value });
                if (!e.target.value) {
                  handleTextChange(field.id, '');
                }
              }}
              onFocus={() => {
                if (!groupSearchTerm) {
                  setFilteredGroups({ ...filteredGroups, [field.id]: groups.slice(0, 10) });
                }
              }}
              disabled={!canEdit || isBlocked}
              required={isRequired}
              readOnly={isBlocked}
              placeholder="Digite o nome do grupo"
            />
            {groupSearchTerm && filteredGroupList.length > 0 && !selectedGroup && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ced4da',
                borderRadius: '0 0 0.375rem 0.375rem',
                maxHeight: '200px',
                overflowY: 'auto',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
                {filteredGroupList.map((group) => (
                  <div
                    key={group.id}
                    onClick={() => {
                      handleTextChange(field.id, group.id.toString());
                      setSearchGroups({ ...searchGroups, [field.id]: '' });
                      setFilteredGroups({ ...filteredGroups, [field.id]: [] });
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #e9ecef',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    {group.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'integer':
        return (
          <Form.Control
            type="number"
            step="1"
            min="0"
            value={displayValue || ''}
            onChange={(e) => {
              const value = e.target.value;
              // Apenas inteiros não negativos
              if (value === '' || /^\d+$/.test(value)) {
                handleTextChange(field.id, value);
              }
            }}
            onWheel={(e) => e.currentTarget.blur()}
            disabled={!canEdit || isBlocked}
            required={isRequired}
            readOnly={isBlocked}
            placeholder="0"
          />
        );

      case 'decimal':
        return (
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            value={displayValue || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || (parseFloat(v) >= 0 && !/^-/.test(v))) {
                handleTextChange(field.id, v);
              }
            }}
            onWheel={(e) => e.currentTarget.blur()}
            disabled={!canEdit || isBlocked}
            required={isRequired}
            readOnly={isBlocked}
            placeholder="0.00"
          />
        );

      case 'currency': {
        let currencyValue = { currency: 'BRL', value: '' };
        if (fieldValue) {
          try {
            currencyValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
          } catch {
            // Se não for JSON válido, usar apenas o valor
            currencyValue = { currency: 'BRL', value: fieldValue || '' };
          }
        }
        
        // Função para obter símbolo da moeda
        const getCurrencySymbol = (currency: string): string => {
          const symbols: { [key: string]: string } = {
            'BRL': 'R$',
            'USD': '$',
            'EUR': '€'
          };
          return symbols[currency] || currency;
        };

        // Função para formatar valor monetário brasileiro
        const formatCurrency = (value: string | number, currency: string = 'BRL'): string => {
          if (!value) return '';
          const numValue = typeof value === 'string' ? parseFloat(value) : value;
          if (isNaN(numValue)) return '';
          const formatted = new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numValue);
          return `${getCurrencySymbol(currency)} ${formatted}`;
        };
        
        return (
          <div className="d-flex gap-2 align-items-center">
            <Form.Select
              value={currencyValue.currency || 'BRL'}
              onChange={(e) => {
                const newCurrency = { ...currencyValue, currency: e.target.value };
                handleTextChange(field.id, JSON.stringify(newCurrency));
              }}
              disabled={!canEdit || isBlocked}
              style={{ width: '150px' }}
            >
              <option value="BRL">BRL (R$)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </Form.Select>
            <Form.Control
              type="text"
              inputMode="decimal"
              value={formatCurrencyInputDisplay(
                currencyValue.value || '',
                currencyValue.currency || 'BRL'
              )}
              onChange={(e) => {
                const normalized = normalizeCurrencyInput(e.target.value);
                if (normalized === null) return;
                const newCurrency = { ...currencyValue, value: normalized };
                handleTextChange(field.id, JSON.stringify(newCurrency));
              }}
              disabled={!canEdit || isBlocked}
              required={isRequired}
              readOnly={isBlocked}
              placeholder="Ex.: 30000,01"
            />
            {currencyValue.value && (
              <Form.Text className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                {formatCurrency(currencyValue.value, currencyValue.currency)}
              </Form.Text>
            )}
          </div>
        );
      }

      case 'calculation': {
        const calcConfig = field.options ? (typeof field.options === 'string' ? JSON.parse(field.options) : field.options) : { fields: [], formula: '' };
        return (
          <div>
            <Form.Control
              type="text"
              value={fieldValue || ''}
              disabled={true}
              readOnly={true}
              placeholder="Resultado do cálculo"
              className="bg-light"
            />
            <Form.Text className="text-muted">
              Campos de cálculo: {calcConfig.fields?.join(', ') || 'Nenhum configurado'} | Fórmula: {calcConfig.formula || 'Não definida'}
            </Form.Text>
          </div>
        );
      }

      case 'check': {
        const options = parseOptions(field.options);
        const hasOptions = options.length > 0;
        const normalizedCheckValue = hasLocalValue ? fieldValue : (existingValue?.text_value ?? fieldValue);

        if (hasOptions) {
          let selectedValues: string[] = [];
          if (normalizedCheckValue) {
            try {
              if (typeof normalizedCheckValue === 'string') {
                const parsed = JSON.parse(normalizedCheckValue);
                if (Array.isArray(parsed)) {
                  selectedValues = parsed.map((v) => String(v));
                } else if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') {
                  selectedValues = [String(parsed)];
                }
              } else if (Array.isArray(normalizedCheckValue)) {
                selectedValues = normalizedCheckValue.map((v) => String(v));
              } else {
                selectedValues = [String(normalizedCheckValue)];
              }
            } catch {
              selectedValues = String(normalizedCheckValue).trim() ? [String(normalizedCheckValue)] : [];
            }
          }

          const selectedValue = selectedValues[0] || '';
          const selectedTokens = selectedValues.map((value) => normalizeOptionToken(value));
          const triggerToken = normalizeOptionToken(field.checkbox_text_trigger_option || '');
          const shouldShowInfoText = field.checkbox_text_trigger_option
            ? (field.allow_multiple
                ? selectedTokens.includes(triggerToken)
                : normalizeOptionToken(selectedValue) === triggerToken)
            : (field.allow_multiple ? selectedValues.length > 0 : !!selectedValue);

          return (
            <div>
              {field.checkbox_link && (
                <div className="mb-2">
                  <a
                    href={field.checkbox_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    {field.checkbox_link_text || 'Ler Diretrizes'}
                  </a>
                </div>
              )}
              <div>
                {options.map((option, idx) => (
                  <Form.Check
                    key={idx}
                    className="custom-field-check"
                    type={field.allow_multiple ? 'checkbox' : 'radio'}
                    name={field.allow_multiple ? undefined : `field-${field.id}`}
                    label={option}
                    checked={
                      field.allow_multiple
                        ? selectedTokens.includes(normalizeOptionToken(option))
                        : normalizeOptionToken(selectedValue) === normalizeOptionToken(option)
                    }
                    onChange={(e) => {
                      if (field.allow_multiple) {
                        const newValues = e.target.checked
                          ? [...selectedValues, option]
                          : selectedValues.filter((v) => v !== option);
                        handleSelectChange(field.id, newValues);
                        return;
                      }
                      handleTextChange(field.id, e.target.checked ? option : '');
                    }}
                    disabled={!canEdit || isBlocked}
                  />
                ))}
              </div>
              {field.checkbox_text && shouldShowInfoText && (
                <Alert variant="info" className="mt-2">
                  {field.checkbox_text}
                </Alert>
              )}
            </div>
          );
        }

        // Checkbox único (comportamento original)
        const isChecked =
          normalizedCheckValue === 'true' ||
          normalizedCheckValue === true ||
          normalizedCheckValue === 1 ||
          normalizedCheckValue === '1';
        return (
          <div>
            {field.checkbox_link && (
              <div className="mb-2">
                <a
                  href={field.checkbox_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  {field.checkbox_link_text || 'Ler Diretrizes'}
                </a>
              </div>
            )}
            <Form.Check
              className="custom-field-check"
              type="checkbox"
              label={field.checkbox_label || field.label}
              checked={isChecked}
              onChange={(e) => {
                handleTextChange(field.id, e.target.checked ? 'true' : 'false');
              }}
              disabled={!canEdit || isBlocked}
            />
            {isChecked && field.checkbox_text && (
              <Alert variant="info" className="mt-2">
                {field.checkbox_text}
              </Alert>
            )}
          </div>
        );
      }

      case 'table': {
        return (
          <CustomTableField
            value={fieldValue || createDefaultTableValue()}
            onChange={(nextTable) => {
              // Armazenar o objeto TableCustomFieldValue no estado
              setValues(prevValues => ({ ...prevValues, [field.id]: nextTable }));
            }}
            disabled={!canEdit || isBlocked}
          />
        );
      }

      default:
        return null;
    }
  };

  if (customFields.length === 0) {
    return null;
  }

  return (
    <Form onSubmit={handleSubmit}>
      {customFields.map((field) => {
        const isVisibleInStep = field.step_is_visible !== false;
        const hasValue = hasStoredValue(field.id);
        const isChecklistField = isChecklistRelatedField(field);
        const isFileTypeField = field.field_type === 'file' || field.field_type === 'files';
        const displayLabel = isChecklistAttachmentField(field) && field.description
          ? field.description
          : field.label;

        // Campos ocultos na etapa só aparecem quando já possuem valor salvo (modo consulta).
        if (!isVisibleInStep && !hasValue) {
          return null;
        }

        // Verificar se o campo deve ser exibido baseado em condições
        if (!shouldShowField(field) && (!hasValue || isChecklistField) && !isFileTypeField) {
          return null;
        }

        const isRequired = !!field.is_required || !!field.step_is_required;
        
        
        return (
        <Form.Group key={field.id} className="mb-3">
          <Form.Label>
            {displayLabel}
            {isRequired && <span className="text-danger ms-1">*</span>}
            <Badge bg="secondary" className="ms-2">
              {isSupplierCustomField(field) ? 'Fornecedor' :
               field.field_type === 'text_short' ? 'Texto' :
               field.field_type === 'text_long' ? 'Texto Longo' :
               field.field_type === 'list' ? 'Lista' :
               field.field_type === 'combo' ? 'Combo' :
               field.field_type === 'file' ? 'Arquivo' :
               field.field_type === 'files' ? 'Arquivos' :
               field.field_type === 'date' ? 'Data' :
               field.field_type === 'period' ? 'Período' :
               field.field_type === 'link' ? 'Link' :
               field.field_type === 'user' ? 'Usuário' :
               field.field_type === 'analyst' ? 'Usuário Analista' :
               field.field_type === 'approver' ? 'Aprovador' :
               field.field_type === 'contract_manager_approver' ? 'Aprovador/Gerente do Contrato' :
               field.field_type === 'group' ? 'Grupo de Usuário' :
               field.field_type === 'integer' ? 'Número Inteiro' :
               field.field_type === 'decimal' ? 'Número Decimal' :
               field.field_type === 'currency' ? 'Financeiro' :
               field.field_type === 'calculation' ? 'Cálculo' :
               field.field_type === 'check' ? 'Checkbox' :
               field.field_type === 'technical_proposal_review' ? 'Análise Técnica de Proposta' :
               field.field_type === 'table' ? 'Tabela' : field.field_type}
            </Badge>
          </Form.Label>
          {field.description && (
            <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line' }}>
              {field.description}
            </Form.Text>
          )}
          {renderField(field)}
        </Form.Group>
        );
      })}
      {canEdit && (
        <div className="d-flex justify-content-end">
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Campos'}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default CustomFieldsEditor;

